import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Status flow reorder (2026-06-10, Yahya): the REAL production order is
// imprimée (printer returns the prints) → en production (we assemble the
// album) → expédiée → livrée. Key insertion order drives every status
// <select>. Stock auto-deduction triggers on the transition to 'printed'.
export const ORDER_STATUS_LABELS: Record<string, string> = {
  'pending-album': 'En attente',
  'album-submitted': 'Album soumis',
  paid: 'Payée',
  'ready-to-print': 'Prêt à imprimer',
  printed: 'Imprimée',
  'in-production': 'En production (fabrication)',
  'ready-to-deliver': 'Prêt à livrer',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée',
  'in-redo': 'En redo',
};

export const ORDER_STATUS_COLORS: Record<string, string> = {
  'pending-album': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'album-submitted': 'bg-blue-100 text-blue-800 border-blue-200',
  paid: 'bg-lime-100 text-lime-800 border-lime-200',
  'ready-to-print': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'in-production': 'bg-orange-100 text-orange-800 border-orange-200',
  printed: 'bg-green-100 text-green-800 border-green-200',
  'ready-to-deliver': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  shipped: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  delivered: 'bg-teal-100 text-teal-800 border-teal-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  'in-redo': 'bg-amber-100 text-amber-800 border-amber-200',
};

export function formatPhone(phone: string | null): string {
  if (!phone) return '—';
  return phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-MA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Date + exact time (24h), e.g. "23 juil. 2026, 14:32". Used in the admin
 *  orders list so staff see precisely when each order came in, not just the day. */
export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('fr-MA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatPrice(price: number | null): string {
  if (price === null) return '—';
  return `${price.toFixed(0)} MAD`;
}

// Canonical Shopify checkout page. The COD form passes this in `?next=…`, but
// when that param is missing (admin testing, direct link, sessionStorage wipe)
// we must still land on the checkout page — never the storefront homepage,
// which used to silently swallow the customer's confirmation.
export const STORIZ_CHECKOUT_URL = 'https://storiz.ma/pages/checkout-storiz';

// Subset of the orders row the Shopify checkout-storiz section reads from
// URL params (storiz-checkout.liquid hydrates the summary from query string
// only — there is no API fetch, so we must serialize every field here).
export interface CheckoutOrderSummary {
  shopify_order_id?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
  bundle?: string | null;
  layout?: string | null;
  page_count?: number | null;
  destination_1?: string | null;
  total_price?: number | null;
  /** Pages beyond the 50-page base allowance, billed at price_per_page.
   *  Surfaced separately so the checkout page can render the breakdown
   *  (base + extras = total) instead of a single opaque total. */
  extra_pages?: number | null;
  extra_pages_cost?: number | null;
  /** Chosen cover name (funnel v2) — flows checkout → thank-you so the
   *  WhatsApp CTA can mention it. */
  cover?: string | null;
  /** FUNNEL V2 × MULTI-ALBUM (2026-07-24): covers of albums 2/3 for
   *  Duo/Trio bundles — the storefront checkout hydrates from URL params
   *  only, so every album's cover must ride the redirect. */
  cover_2?: string | null;
  cover_3?: string | null;
}

// Storiz checkout origin (Shopify storefront). The COD form passes its
// `?next=` value as a *relative* path on its own origin (storiz.ma); when
// the editor running on app.storiz.ma copies that into window.location.href,
// the browser resolves it against app.storiz.ma → no such page → router
// fallback → /login. Always force the checkout into the storefront origin.
const STORIZ_STOREFRONT_ORIGIN = 'https://storiz.ma';

export function getCheckoutRedirect(
  nextUrl: string | null | undefined,
  order: string | CheckoutOrderSummary | null | undefined,
): string {
  let base = nextUrl && nextUrl.length > 0 ? nextUrl : STORIZ_CHECKOUT_URL;
  // Relative path → absolute on the storefront origin.
  if (base.startsWith('/')) {
    base = STORIZ_STOREFRONT_ORIGIN + base;
  }

  // Back-compat: legacy callers passed just the order id string.
  const summary: CheckoutOrderSummary | null =
    typeof order === 'string'
      ? { shopify_order_id: order }
      : order ?? null;

  if (!summary) return base;

  const qs = new URLSearchParams();
  const put = (k: string, v: string | number | null | undefined) => {
    if (v === null || v === undefined || v === '') return;
    qs.set(k, String(v));
  };
  put('order_id',         summary.shopify_order_id);
  put('name',             summary.customer_name);
  put('phone',            summary.customer_phone);
  put('address',          summary.customer_address);
  put('bundle',           summary.bundle);
  put('layout',           summary.layout);
  put('page_count',       summary.page_count);
  put('destination',      summary.destination_1);
  put('total',            summary.total_price);
  put('extra_pages',      summary.extra_pages);
  put('extra_pages_cost', summary.extra_pages_cost);
  put('cover',            summary.cover);
  put('cover_2',          summary.cover_2);
  put('cover_3',          summary.cover_3);

  const qsStr = qs.toString();
  if (!qsStr) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}${qsStr}`;
}

// Thank-you page on the Shopify storefront. The IN-APP checkout step
// (AlbumFlowPage StepCheckout) redirects here directly after
// /checkout-confirm — skipping /pages/checkout-storiz entirely, whose job
// the in-app step now does. thank-you-content.liquid hydrates from URL
// params exactly like the checkout page did, so we serialize the same
// fields + payment_method + the FINAL (real, post-draft-promotion) order id.
const STORIZ_THANK_YOU_URL = 'https://storiz.ma/pages/thank-you';

export function getThankYouRedirect(
  order: CheckoutOrderSummary | null | undefined,
  finalOrderId: string | null | undefined,
  paymentMethod: 'cod' | 'virement',
): string {
  const qs = new URLSearchParams();
  const put = (k: string, v: string | number | null | undefined) => {
    if (v === null || v === undefined || v === '') return;
    qs.set(k, String(v));
  };
  put('order_id',         finalOrderId || order?.shopify_order_id);
  put('name',             order?.customer_name);
  put('bundle',           order?.bundle);
  put('layout',           order?.layout);
  put('page_count',       order?.page_count);
  put('destination',      order?.destination_1);
  put('total',            order?.total_price);
  put('extra_pages',      order?.extra_pages);
  put('extra_pages_cost', order?.extra_pages_cost);
  put('cover',            order?.cover);
  put('payment_method',   paymentMethod);
  const qsStr = qs.toString();
  return qsStr ? `${STORIZ_THANK_YOU_URL}?${qsStr}` : STORIZ_THANK_YOU_URL;
}
